import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CoinCard } from './CoinCard';
import type { ArtRenderer, CoinView } from './model';

type Selection = { id: string; name: string; trigger: HTMLButtonElement };
const CoinModalContext = createContext<{
  isOpen: boolean;
  open: (coin: CoinView, trigger: HTMLButtonElement) => void;
} | null>(null);

export const useCoinModal = () => useContext(CoinModalContext);

export function CoinModalProvider({
  coins,
  now,
  art,
  children,
}: {
  coins: CoinView[];
  now: number;
  art: ArtRenderer;
  children: ReactNode;
}) {
  const [selection, setSelection] = useState<Selection | null>(null);
  return (
    <CoinModalContext.Provider
      value={{
        isOpen: selection !== null,
        open: (coin, trigger) =>
          setSelection({ id: coin.id, name: coin.name, trigger }),
      }}
    >
      {children}
      {selection && (
        <CoinModal
          // Resolve from the full feed on every tick, not the six visible slots.
          // Never retain a stale quote or invent a result when a round disappears.
          coin={coins.find((coin) => coin.id === selection.id)}
          name={selection.name}
          trigger={selection.trigger}
          now={now}
          art={art}
          onClose={() => setSelection(null)}
        />
      )}
    </CoinModalContext.Provider>
  );
}

export function CoinModal({
  coin,
  name,
  trigger,
  now,
  art,
  onClose,
}: {
  coin: CoinView | undefined;
  name: string;
  trigger: HTMLButtonElement;
  now: number;
  art: ArtRenderer;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const backdropPress = useRef(false);
  const titleId = useId();
  useEffect(() => {
    const node = dialog.current!;
    const root = document.documentElement;
    const body = document.body;
    const overflow = root.style.overflow;
    const bodyOverflow = body.style.overflow;
    const gutter = root.style.scrollbarGutter;
    root.style.scrollbarGutter = 'stable';
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    node.showModal();
    closeButton.current?.focus({ preventScroll: true });
    return () => {
      node.close();
      root.style.overflow = overflow;
      body.style.overflow = bodyOverflow;
      root.style.scrollbarGutter = gutter;
      // A newly arriving coin can evict the opener while this dialog is open.
      const target = trigger.isConnected
        ? trigger
        : document.querySelector<HTMLButtonElement>('.nav-pill button');
      target?.focus({ preventScroll: true });
    };
  }, [trigger]);
  return (
    // Native dialog's backdrop is pointer-only; its keyboard equivalent is onCancel (Escape).
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialog}
      className="coin-modal"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={(event) => {
        if (!event.currentTarget.open) onClose();
      }}
      onPointerDown={(event) => {
        backdropPress.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (backdropPress.current && event.target === event.currentTarget)
          onClose();
        backdropPress.current = false;
      }}
    >
      <div className="coin-modal-shell">
        <button
          ref={closeButton}
          type="button"
          className="coin-modal-close"
          aria-label="Close coin details"
          onClick={onClose}
        >
          <img
            src="/images/rail-modal-close.svg"
            width="24"
            height="24"
            alt=""
          />
        </button>
        <div className="coin-modal-panel coin-swap is-cascade">
          {coin ? (
            <CoinCard coin={coin} now={now} art={art} titleId={titleId} />
          ) : (
            <div className="coin-modal-unavailable" role="status">
              <h2 id={titleId}>{name}</h2>
              <p>This coin is no longer available in the current feed.</p>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
